import { ExceptionService } from '../services/exception.service';
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { NodeAPIEntityService } from 'app/services/node.entity.service';
import { TogetherAuctionDIPEntityType } from './../models/enums';
import { DipStateService } from 'app/services/dip-state-service.service';
import { NodeAPIService } from 'app/services/node.api.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'recap',
    templateUrl: './recap.component.html',
    styleUrls: ['./recap.component.scss'],
    standalone: false
})
export class RecapComponent implements OnInit {
  /**
   * The ID of the case to be recapped
   *
   * @type {string}
   * @memberof RecapComponent
   */
  @Input() caseId: string;
  /**
   * Case summary data
   *
   * @private
   * @type {object}
   * @memberof RecapComponent
   */
  private data: object;


  /**
   * The recap form. I have made this a group in the likely scenario this expands.
   *
   * @private
   * @type {FormGroup}
   * @memberof RecapComponent
   */
  private recapForm: UntypedFormGroup = new UntypedFormGroup({
    applicantDetails: new UntypedFormArray([]),
  });

  /**
   * Notifies the parent component that the stage needs to change
   *
   * @type {EventEmitter<string>}
   * @memberof RecapComponent
   */
  @Output() stageChanging: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Perform address matching on applicants
   *
   * @type {boolean}
   * @memberof RecapComponent
   */
  @Input() doAddressMatch: boolean = false;

  /**
   * Perform address matching on applicants
   *
   * @type {boolean}
   * @memberof RecapComponent
   */
  @Input() addressMatchDoneAlready: boolean = false;
  /**
   * shwitches the edit buttons of any sub-somponents on/off
   *
   * @type {boolean}
   * @memberof RecapComponent
   */
  @Input() showEditButtons: boolean = true;

  /**
   * Output the recap form.
   *
   * @type {EventEmitter<FormArray>}
   * @memberof RecapComponent
   */
  @Output() recapFormOut: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();
  /**
   * Is the data loaded from the API
   *
   * @type {boolean}
   * @memberof RecapComponent
   */
  isDataLoaded: boolean = false;

  /**
   * The applicants form controls.
   *
   * @type {FormArray}
   * @memberof DipOverviewComponent
   */
  applicants: UntypedFormArray = new UntypedFormArray([]);

  /**
   * Loan details from data
   *
   * @readonly
   * @memberof RecapComponent
   */
  get LoanDetails() {
    if (_.isEmpty(this.data)) {
      return;
    }

    return _.defaultTo(this.data['LoanDetails'], null);
  }
  /**
   * Applicant details from data
   *
   * @readonly
   * @memberof RecapComponent
   */
  get ApplicantDetails() {
    if (_.isEmpty(this.data)) {
      return;
    }
    const apps = {
      IsCompanyApplication: _.defaultTo(this.data['IsCompanyApplication'], false)
        ? 'Yes' : 'No',
      Applicants: this.Applicants,
    };
    return apps;
  }
  /**
   * Details of the property being purchased
   *
   * @readonly
   * @memberof RecapComponent
   */
  get PropertyDetails() {
    if (_.isEmpty(this.data)) {
      return;
    }
    const details = _.defaultTo(this.data['PropertyDetails'], null);

    if (details['ValuationAmount'] === 0) {
      details['ValuationAmount'] = this.LoanDetails['PropertyValuation'];
    }
    return details;
  }
  /**
   * Income data for the applicants
   *
   * @readonly
   * @memberof RecapComponent
   */
  get IncomeDetails() {
    if (_.isEmpty(this.data)) {
      return;
    }

    const inc = [];
    _.forEach(this.Applicants, (app) => {
      const obj = {};
      obj['Title'] = app.Title;
      obj['FirstName'] = app.FirstName;
      obj['MiddleName'] = app.MiddleName;
      obj['Surname'] = app.Surname;
      obj['IncomeDetails'] = app.IncomeDetails;
      inc.push(obj);
    });

    return inc;
  }
  /**
   * Applicant details from the data
   *
   * @readonly
   * @memberof RecapComponent
   */
  get Applicants() {
    if (_.isEmpty(this.data)) {
      return;
    }
    return _.defaultTo(this.data['IsCompanyApplication'], false) === true
      ? _.defaultTo(this.data['ApplicantDetails'].filter(x => x.ApplicantSequence !== 1), '')
      : _.defaultTo(this.data['ApplicantDetails'], '');
  }
  /**
   * Additional loan details form the data
   *
   * @readonly
   * @memberof RecapComponent
   */
  get AdditionalDetails() {
    if (_.isEmpty(this.data)) {
      return;
    }

    return _.defaultTo(this.data['AdditionalSecurity'], {});
  }
  /**
   * Solicitor details form the data
   *
   * @readonly
   * @memberof RecapComponent
   */
  get SolicitorDetails() {
    if (_.isEmpty(this.data)) {
      return;
    }
    return _.defaultTo(this.data['SolicitorDetails'], {});
  }
  /**
   * The step the application is up to
   *
   * @readonly
   * @memberof RecapComponent
   */
  get CurrentStep() {
    if (_.isEmpty(this.data)) {
      return;
    }

    return _.toInteger(this.data['CurrentStepId']);
  }
  /**
   * Does the applicants have any outstanding Equifax address matches
   *
   * @readonly
   * @type {boolean}
   * @memberof RecapComponent
   */
  get HasOutstandingAddressMatches(): boolean {
    let found = false;
    _.forEach(this.Applicants, (app) => {
      _.forEach(app['Addresses'], (add) => {
        if (_.isNil(add['PtcAbsCode'])) {
          found = true;
        }
      });
    });
    return found;
  }

  /**
   * Initialise the component
   *
   * @memberof RecapComponent
   */
  ngOnInit() {
    this.loadData();
  }


  /**
   * Updates the form.
   *
   * @param {any} form
   * @memberof RecapComponent
   */
  public receiveApplicant(form) {
    this.recapForm.removeControl('applicantDetails');
    this.recapForm.addControl('applicantDetails', form as UntypedFormArray);
    this.recapFormOut.emit(this.recapForm);
  }

  /**
   * Load required data from the API
   *
   * @private
   * @memberof RecapComponent
   */
  private loadData(): void {
    this.state.IsLoading = true;
    this.entity.getAllEntities(TogetherAuctionDIPEntityType.GetRecap, this.caseId)
      .subscribe(
        (success) => {
          this.data = (success as any);
          this.state.IsLoading = false;
          if (this.doAddressMatch) {
            this.loadAddressMatches();
          } else {
            this.isDataLoaded = true;
            this.state.IsLoading = false;
          }
        },
        (error) => {
          this.exception.haveReceivedError(error);
          this.state.IsLoading = false;
        },
      );
  }
  /**
   * Load Exquifax address matches from the API
   *
   * @private
   * @memberof RecapComponent
   */
  private loadAddressMatches(): void {
    this.state.IsLoading = true;
    this.entity.matchAllAddresses(this.caseId)
      .subscribe(
        (success) => {
          this.mapAddressMatchesToApplicants(success);
          this.isDataLoaded = true;
          this.state.IsLoading = false;
        },
        (error) => {
          this.exception.haveReceivedError(error);
          this.state.IsLoading = false;
        },
      );
  }
  /**
   * Map Equifax addresses to be matched onto the applicants
   *
   * @memberof RecapComponent
   */
  mapAddressMatchesToApplicants(addressMatches): void {
    if (!_.isNil(this.data['ApplicantDetails'])
      && !_.isNil(addressMatches['Applicants'])) {
      if (addressMatches.EquifaxAddressMatch === 4) {
        this.addressMatchDoneAlready = true;
      }
      const addresses = addressMatches['Applicants'];
      // Loop applicants
      _.forEach(this.data['ApplicantDetails'], (app) => {

        // Find address match data for applicant
        const matches = _.find(addresses, (i) => {
          return i.ApplicantId === app.Id;
        });
        if (_.isNil(matches)) return;

        // Loop addresses
        _.forEach(app['Addresses'], (add) => {
          // Find address matches for address
          const match = _.find(matches['Addresses'], (m) => {
            return add.Id === m.AddressId;
          });
          // If there's only one match for the address assign the PtcAbsCode
          if (match['MatchingAddresses'].length === 1) {
            const singleMatch = match['MatchingAddresses'][0];
            add.PtcAbsCode = singleMatch['PtcAbsCode'];
            this.api.saveAddressMatch(add.Id, singleMatch['PtcAbsCode'])
              .subscribe(
                (success) => {
                  add.PtcAbsCode = singleMatch['PtcAbsCode'];

                },
                (error) => {
                  add.AddressMatches = match['MatchingAddresses'];
                },
              );
            // *TODO*: Add API call to map into database
          } else {
            add.AddressMatches = match['MatchingAddresses'];
          }
        });
      });
    }
  }
  /**
   * Notifies the evemt emitter that the stage has changed.
   *
   * @param {any} stage
   * @memberof RecapComponent
   */
  changeStage(stage): void {
    this.stageChanging.emit(stage);
  }

  /**
   * Creates an instance of RecapComponent.
   * @param {NodeAPIEntityService} entity
   * @memberof RecapComponent
   */
  constructor(private entity: NodeAPIEntityService,
    private state: DipStateService,
    private api: NodeAPIService,
    private exception: ExceptionService) { }
}
